import { I18n } from '@ies/starling_intl';
import axios, { AxiosResponse, ResponseType } from 'axios';

import { Message } from '@arco-design/web-react';

import { NODE_ORIGIN } from '@/utils/env';
import { defaultLang } from '@/common/starling';

/** 本地Axios请求实例 */
export const axiosInstance = axios.create({
  baseURL: NODE_ORIGIN,
  timeout: 80000,
  withCredentials: true, // 默认false不带cookie，请求时带上cookie
});

/** 请求错误时的类型 */
type ResponseBodyError = {
  code?: number;
  message?: string;
  msg?: string;
};

/** 请求时的附加属性 */
export type RequestOptions = {
  toastDeny?: boolean;
  toastErrMsg?: string;
  timeout?: number;
  downloadFile?: boolean;
  noErrorMessage?: boolean;
};

/** Axios请求方法(给ferry使用) */
export const axiosFetch = async <RequestBody, ResponseBody>(
  url: string,
  request: {
    method?: 'GET' | 'POST';
    headers?: Record<string, string>;
    body?: RequestBody;
    responseType?: ResponseType;
  },
  options?: RequestOptions,
) => {
  try {
    const response = await axiosInstance.request<ResponseBody>({
      url,
      method: request.method || 'POST',
      headers: {
        ...request.headers,
        'cache-control': 'no-cache',
        'gf-lang': defaultLang,
      },
      responseType: request.responseType,
      data: request.body,
      timeout: options?.timeout,
      validateStatus: code => code >= 200 && code < 300,
    });
    if ((response.data as any)?.code > 0) {
      throw new ErrorAxios(
        I18n.t(
          'the_request_was_successful__the_status_is_not_0',
          {},
          '请求成功,状态非0',
        ),
        response,
      );
    } else {
      return response.data.data;
    }
  } catch (err) {
    const {
      response: { data },
    } = err;

    const response = err.response as
      | AxiosResponse<ResponseBodyError>
      | undefined;
    // 用户未登录
    if (response?.status === 401) {
      if (
        ![
          '/login',
          '/signout',
          '/',
          '/download',
          '/noPermission/download',
        ].includes(window.location.pathname)
        // !window.location.pathname.includes('/share') &&
        // !window.location.pathname.includes('/summary')
      ) {
        localStorage.setItem(
          'redirect_path',
          window.location.pathname + window.location.search,
        );
        window.location.replace('/signout');
      }
    }

    // 418 无权限报错
    if (response?.status === 418) {
      const key = data.right_key;
      const projectID = data.project_id;
      const roles = ['role_member', 'role_temporary_member'];
      // axiosInstance
      //   .get(`/v1/access_control/right/role?right_key=${key}`)
      //   .then(res => {
      //     const roles = res.data.data?.roles;
      //     if (roles.length) {
      //       if (!window.location.pathname.includes('/role-permissions-apply')) {
      //         localStorage.removeItem('redirect_path');
      //         window.location.replace(
      //           `${
      //             window.location.origin
      //           }/role-permissions-apply/${projectID}/${JSON.stringify(roles)}`,
      //         );
      //       }
      //     }
      //   })
      //   .catch(_ => {});
      if (!window.location.pathname.includes('/role-permissions-apply')) {
        localStorage.removeItem('redirect_path');
        window.location.replace(
          `${
            window.location.origin
          }/role-permissions-apply/${projectID}/${JSON.stringify(roles)}`,
        );
      }
    }
    // issue_platform 302 url.includes('issue_platform')
    if (response?.status === 302 ) {
      window.location.href = response.data;
    }

    // 如果没有提供 `noErrorMessage` 标记默认进行全局错误提示
    // 手动关闭请求，关闭报错
    if (err.message === 'cancleRequest') {
      Message.clear();
    } else if (!options?.noErrorMessage) {
      Message.clear(); // 关闭其他全局提示，如有
      Message.error(data?.status?.message || err.response.statusText);
    }

    const errMsg =
      response?.data?.message ||
      `${I18n.t(
        'request_failed__{placeholder1}',
        {
          placeholder1:
            response?.data?.msg ||
            response?.data?.code ||
            response?.status ||
            I18n.t('network_error', {}, '网络错误'),
        },
        '请求失败: {placeholder1}',
      )}`;
    // throw new Error();
    return Promise.reject(err);
  }
};

/** 本地Axios请求错误类型 */
export class ErrorAxios extends Error {
  response?: AxiosResponse<ResponseBodyError>;

  constructor(message: string, response?: AxiosResponse<ResponseBodyError>) {
    super(message);
    this.response = response;
  }
}
